import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { functions, db } from './utils/firebase'
import { httpsCallable } from "firebase/functions";
import { Container } from 'react-bootstrap';
import { collection, getDocs, collectionGroup, query, where } from 'firebase/firestore';
import moment from 'moment';
import 'moment/locale/nl';
import { useIframeRefresh } from './hooks/useIframeRefresh';

function App(props) {
    const url_session_id = new URLSearchParams(useLocation().search).get('uid');
    const [loading, setLoading] = useState(false)
    const [user, setUser] = useState()
    const [purchases, setPurchases] = useState(null)
    const [series, setSeries] = useState([])
    const [seriesLoading, setSeriesLoading] = useState(false)
    const [testUser, setTestUser] = useState(null)
    useIframeRefresh();
    // const routes = props.routes

    // Storage helper functions for incognito compatibility
    const setSessionId = (sessionId) => {
        // Always use sessionStorage (works in incognito)
        sessionStorage.setItem('ukies-webinars-session', sessionId);
        
        // Try localStorage as backup (for non-incognito persistence)
        try {
            localStorage.setItem('ukies-webinars-session', sessionId);
        } catch (e) {
            // console.log('localStorage not available, using sessionStorage only');
        }
    };

    const getSessionId = () => {
        // Try sessionStorage first (works in incognito), then localStorage
        return sessionStorage.getItem('ukies-webinars-session') || 
               localStorage.getItem('ukies-webinars-session');
    };

    const removeSessionId = () => {
        sessionStorage.removeItem('ukies-webinars-session');
        try {
            localStorage.removeItem('ukies-webinars-session');
        } catch (e) {
            // localStorage might not be available
        }
    };

    // Storage helper functions for opt-in email
    const getOptinEmail = () => {
        // Try sessionStorage first (works in incognito), then localStorage
        return sessionStorage.getItem('ukies-webinars-optin-email') || 
               localStorage.getItem('ukies-webinars-optin-email');
    };

    const removeOptinEmail = () => {
        sessionStorage.removeItem('ukies-webinars-optin-email');
        try {
            localStorage.removeItem('ukies-webinars-optin-email');
        } catch (e) {
            // localStorage might not be available
        }
        };

    // Function to check if user is a test user
    const checkTestUser = async (customerId) => {
        if (!customerId) {
            setTestUser(null);
            return;
        }

        try {
            const testUsersQuery = query(
                collection(db, 'testUsers'), 
                where('customerId', '==', customerId),
                where('enabled', '==', true)
            );
            
            const testUsersSnapshot = await getDocs(testUsersQuery);
            
            if (!testUsersSnapshot.empty) {
                const testUserData = testUsersSnapshot.docs[0].data();
                setTestUser(testUserData);
                // console.log('Test user found:', testUserData);
            } else {
                setTestUser(null);
            }
        } catch (error) {
            console.error('Error checking test user:', error);
            setTestUser(null);
        }
    };

    useEffect(() => {
        const stored_session_id = getSessionId();
        
        // Use URL parameter if available, otherwise use stored session
        const session_id = url_session_id || stored_session_id;
        
        if(session_id && session_id.length > 0 && !(user && user.customerId)) {
            // Store session_id if it came from URL
            if(url_session_id) {
                setSessionId(url_session_id);
            }
            
            setLoading(true)
            const fetchCustomerData = httpsCallable(functions, 'ukiesFetchCustomerData');
            fetchCustomerData({session_id: session_id})
            .then(async (result) => {
                let userData = result.data.user;
                
                console.log('userData', userData)
                // Check session opt-in status before setting user
                if (userData && userData.optedIn === false) {
                    const sessionOptinEmail = getOptinEmail();
                    
                    if (sessionOptinEmail) {
                        try {
                            const ukiesCheckOptinStatus = httpsCallable(functions, 'ukiesCheckOptinStatus');
                            const optinResult = await ukiesCheckOptinStatus({
                                email: sessionOptinEmail
                            });
                            console.log('optinResult', optinResult)
                            
                            if (optinResult.data === true) {
                                // Update userData with opt-in status
                                userData = {
                                    ...userData,
                                    optedIn: true,
                                    optedInEmail: sessionOptinEmail
                                };
                            } else {
                                // Remove session variable if opt-in is false
                                removeOptinEmail();
                            }
                        } catch (error) {
                            console.error('Error checking opt-in status:', error);
                            // Remove session variable on error
                            removeOptinEmail();
                        }
                    }
                }
                
                // Update useStates with potentially modified userData
                setUser(userData)
                setPurchases(result.data.purchases || [])
                setLoading(false)
                
                // Check if user is a test user
                checkTestUser(userData?.customer_id)
                if(result.data.status == "error") {
                    // Session expired, remove it
                    removeSessionId();
                }
            })
            .catch(async (error) => {
                // Remove stored session on authentication failure
                removeSessionId();
                
                // Check for opt-in email even when authentication fails
                let userData = {optedIn: false};
                const sessionOptinEmail = getOptinEmail();
                
                if (sessionOptinEmail) {
                    try {
                        const ukiesCheckOptinStatus = httpsCallable(functions, 'ukiesCheckOptinStatus');
                        const optinResult = await ukiesCheckOptinStatus({
                            email: sessionOptinEmail
                        });
                        
                        if (optinResult.data === true) {
                            // Update userData with opt-in status
                            userData = {
                                ...userData,
                                optedIn: true,
                                optedInEmail: sessionOptinEmail
                            };
                        } else {
                            // Remove session variable if opt-in is false
                            removeOptinEmail();
                        }
                    } catch (optinError) {
                        console.error('Error checking opt-in status:', optinError);
                        // Remove session variable on error
                        removeOptinEmail();
                    }
                }
                
                setUser(userData);
                setPurchases([]);
                setLoading(false);
                
                // Check if user is a test user (though unlikely in error case)
                checkTestUser(userData?.customer_id);
            });
        } else if (!session_id) {
            // No session available, but check for opt-in email
            removeSessionId();
            
            const checkAnonymousOptin = async () => {
                let userData = {optedIn: false};
                const sessionOptinEmail = getOptinEmail();
                
                if (sessionOptinEmail) {
                    try {
                        const ukiesCheckOptinStatus = httpsCallable(functions, 'ukiesCheckOptinStatus');
                        const optinResult = await ukiesCheckOptinStatus({
                            email: sessionOptinEmail
                        });
                        
                        if (optinResult.data === true) {
                            // Update userData with opt-in status
                            userData = {
                                ...userData,
                                optedIn: true,
                                optedInEmail: sessionOptinEmail
                            };
                        } else {
                            // Remove session variable if opt-in is false
                            removeOptinEmail();
                        }
                    } catch (error) {
                        console.error('Error checking opt-in status:', error);
                        // Remove session variable on error
                        removeOptinEmail();
                    }
                }
                
                setUser(userData);
                setPurchases([]);
                setLoading(false);
                
                // Check if user is a test user
                checkTestUser(userData?.customer_id);
            };
            
            checkAnonymousOptin();
        }
    },[])

    useEffect(() => {
        const getSeries = async () => {
            setSeriesLoading(true)
            
            try {
                // Fetch all series, modules, and files in parallel
                const [seriesSnapshot, modulesSnapshot, filesSnapshot] = await Promise.all([
                    getDocs(collection(db, 'series')),
                    getDocs(collectionGroup(db, 'modules')),
                    getDocs(collectionGroup(db, 'files'))
                ]);

                // Process series
                const result = [];
                for (const doc of seriesSnapshot.docs) {
                    const seriesItem = doc.data();
                    
                    if(seriesItem.enabled) {
                        seriesItem.id = doc.id;
                        seriesItem.modules = []; // Initialize empty arrays
                        seriesItem.files = [];
                        result.push(seriesItem);
                    }
                }

                // Create a map for quick series lookup
                const seriesMap = new Map(result.map(series => [series.id, series]));

                // Process modules and add to corresponding series
                modulesSnapshot.docs.forEach(doc => {
                    const moduleData = doc.data();
                    const seriesId = doc.ref.parent.parent.id; // Get parent series ID
                    
                    if (seriesMap.has(seriesId)) {
                        seriesMap.get(seriesId).modules.push(moduleData);
                    }
                });

                // Process files and add to corresponding series
                filesSnapshot.docs.forEach(doc => {
                    const fileData = doc.data();
                    const seriesId = doc.ref.parent.parent.id; // Get parent series ID
                    
                    if (seriesMap.has(seriesId)) {
                        seriesMap.get(seriesId).files.push(fileData);
                    }
                });

                setSeries(updateOwnership(result))
            } catch (error) {
                console.error('Error fetching series data:', error);
                setSeries([]);
            } finally {
                setSeriesLoading(false)
            }
        }
        
        const updateOwnership = (series) => {
            const updatedSeries = series.map(s => ({ ...s })); // Create a copy to avoid mutations
            updatedSeries.forEach((s) => {
                // Access if they purchased the product and it's availabilityInMonths hasn't expired yet.
                // With multiple purchases of the same product (e.g. renewals), the latest expiry date counts.
                const expiryDates = purchases
                    .filter(p => p.product_id === s.productId)
                    .map(p => moment(p.created_at).add(s.availabilityInMonths, 'month'))
                const latestExpiry = expiryDates.length > 0 ? moment.max(expiryDates) : null
                s.access = latestExpiry !== null && latestExpiry.isAfter(moment())
                s.accessRemaining = s.access ? Math.max(0, Math.round(latestExpiry.diff(moment(), 'days', true))) : 0

                // OR if user is a test user and has access to this series
                if(testUser && testUser.enabled && testUser.seriesEnabled && Array.isArray(testUser.seriesEnabled)) {
                    const hasTestAccess = testUser.seriesEnabled.some(seriesRef => {
                        // Handle both string UUIDs and object references
                        const seriesUuid = typeof seriesRef === 'string' ? seriesRef : (seriesRef.uuid || seriesRef.id);
                        return seriesUuid === s.uuid;
                    });
                    
                    if(hasTestAccess) {
                        s.access = true;
                        s.accessRemaining = 10000; // Set high number for test access
                    }
                }

                // OR if product is free
                if(s.isFree) {
                    s.access = true
                    s.accessRemaining = 10000
                }
            })
            
            // Sort array
            updatedSeries.sort((a,b) => (((a.access === b.access)? 0 : a.access? -1 : 1) || a.accessRemaining - b.accessRemaining || b.id - a.id))
            return updatedSeries
        }

        // Wait for purchases to be loaded before fetching series (because we need to check if the user has access to the series, which requires purchase)
        if(purchases) {
            if(series.length < 1 && !loading) { 
                getSeries(); 
            } else if(series.length > 0) {
                // Re-evaluate ownership when testUser changes
                setSeries(updateOwnership(series));
            }
        }
    },[purchases, loading, testUser])

  return (
    <Container className='px-3 lg:px-8 mx-auto' style={{maxWidth:'80rem'}}>
      <Outlet context={{routes:props.routes, user, setUser, purchases, loading, series, setSeries, seriesLoading, testUser}}/>
    </Container>
  );
};

export default App;